import { AssetInfoTypes } from "threngine";
import { Controller3D } from "./3DController";
import { Mesh, MeshBasicMaterial, Material, Object3D } from "three";
import { ConfiguratorModel } from "./ConfiguratorModel";
import { CarModelCameraList, CarModelConfig, ModelLoadController, CarXRay } from "./ModelLoad/ModelLoadController";
import { trackVisualFeature } from "../Tracking";
import { XREngine, XRStatus } from "../ARController/XREngine";

export enum XRayLocation {
  None = "none",
  Car = "car",
  Seat = "seat",
  Trunk = "trunk",
  Battery = "battery"
}

const LOCATIONS:XRayLocation[] = [
  XRayLocation.Car,
  XRayLocation.Seat,
  XRayLocation.Trunk,
  XRayLocation.Battery
];

export class XRayController {
  
  private _active:boolean = false;
  private _location:XRayLocation = XRayLocation.None;
  private turnedLightOff:boolean = false;
  private replacedMeshMaterials:Map<number, Material|Material[]>;
  private hiddenNodes:Object3D[] = [];
  private shadowMeshes:Object3D[] = [];
  private shadowCapMeshes:Object3D[] = [];
  private currentGroupIDs:string[];
  private config:CarXRay|undefined;
  private environmentAudioMuteToken:any|null = null;

  public get active():boolean { return this._active; }
  public get location():XRayLocation { return this._location; }
  
  public constructor(
    private controller3D:Controller3D,
    private modelController:ConfiguratorModel
  ) {
    ModelLoadController.event.addListener(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this.config = config.xray;
    });
  }

  public initialize():void {
    this.controller3D.arController.xrEngine.event.on(XREngine.EVENT_STATUS_CHANGE, (status:XRStatus) => {
      if (this.active && status == XRStatus.Stopped) {
        // Change background to X-Ray setup.
        // XR Engine will change environment too... that's supposed to happen first,
        // but somehow it doesn't... so we use the miniscule delay to work around it.
        setTimeout(() => this.changeEnvironment(), 10);
      }
    });
  }

  public isEnabledLocation(location:XRayLocation):boolean {
    if (!this.config || !this.config.enabled) {
      return true;
    }
    return this.config.enabled.indexOf(location) != -1;
  }

  public async enter():Promise<void> {
    const scene = this.controller3D.engine.scene;
    const assets = this.controller3D.engine.assetProvider.getAssets();
    const services = this.controller3D.engine.engine.services;

    if (this.active) {
      return;
    }

    if (this.modelController.environmentAudioSource) {
      this.environmentAudioMuteToken = this.modelController.environmentAudioSource.acquireMuteToken();
    }

    trackVisualFeature("open x-ray");

    const materialAsset = assets.find(
      a => a.infoType === AssetInfoTypes.Material &&
      a.info.name === "XRay Material (New)");

    const material:Material = (await (services.material as any)
      ._getMaterial(materialAsset.id))
      .getMaterials()[0];

    material.depthWrite = false;

    // const interior = scene.getObjectByName("INTERIOR");
    // const exterior = scene.getObjectByName("EXTERIOR");
    const parents = this.config.translucentNodes.map(name => scene.getObjectByName(name));
    const wheels = scene.getObjectByName("WHEELS");

    this.replacedMeshMaterials = new Map();

    for (const parent of parents) {
      parent.traverse(c => {
        if (c instanceof Mesh) {
          // if (c.material instanceof MeshBasicMaterial) {
          //   if (c.name.indexOf("Shadow") != -1) {
          //     c.renderOrder = -1;
          //     this.shadowMeshes.push(c);
          //   }  
          //   return;
          // }
          if (((c.material instanceof MeshBasicMaterial) || (<any>c.material).isXRShadowMaterial) && c.name.indexOf("Shadow") != -1) {
            c.renderOrder = -1;
            this.shadowMeshes.push(c);
            return;
          }

          this.replacedMeshMaterials.set(c.id, c.material);
          c.material = material;
          c.renderOrder = 1;
        }
      });
    }

    wheels.traverse(c => {
      if (c.name.indexOf("Shadowcap") != -1) {
        c.visible = false;
        this.shadowCapMeshes.push(c);
        return;
      }
    })

    if (this.modelController.isLightOn()) {
      this.modelController.turnLight();
      this.turnedLightOff = true;
    } else {
      this.turnedLightOff = false;
    }

    this.hiddenNodes = this.config.hiddenNodes.map(n => scene.getObjectByName(n));

    for (const hiddenNode of this.hiddenNodes) {
      if (hiddenNode) {
        hiddenNode.visible = false;
      }
    }

    // Need to make sure that this happens after the light changes the background.
    setTimeout(() => this.changeEnvironment(), 10);

    this.currentGroupIDs = [];
    this._active = true;
  }

  public leave():void {
    if (!this.active) {
      return;
    }

    if (this.modelController.environmentAudioSource && this.environmentAudioMuteToken) {
      this.modelController.environmentAudioSource.releaseMuteToken(this.environmentAudioMuteToken);
      this.environmentAudioMuteToken = null;
    }

    for (const key of this.replacedMeshMaterials.keys()) {
      const object = this.controller3D.engine.scene.getObjectById(key) as Mesh;

      object.material = this.replacedMeshMaterials.get(key);
      object.renderOrder = 0;
    }

    if (!this.modelController.isLightOn() && this.turnedLightOff) {
      this.modelController.turnLight();
    }
    
    for (const hiddenNode of this.hiddenNodes) {
      if (hiddenNode) {
        hiddenNode.visible = true;
      }
    }

    for (const shadowCapMesh of this.shadowCapMeshes) {
      shadowCapMesh.visible = true;
    }
    this.shadowCapMeshes.length = 0;

    for (const shadowMesh of this.shadowMeshes) {
      shadowMesh.renderOrder = 0;
    }
    this.shadowMeshes.length = 0;

    this.controller3D.engine.configurator.setVisible(this.currentGroupIDs, false);

    // Restore original background and environment.
    const envSelectionGroup = this.modelController.environmentGroup;
    const currentEnvID = envSelectionGroup.selections[envSelectionGroup.currentSelection].id;
    this.modelController.changeSelection(envSelectionGroup.groupID, currentEnvID);

    this.controller3D.setCamera(CarModelCameraList.Orbit);
    this._location = XRayLocation.None;  
    this._active = false;
  }

  public async setLocation(location:XRayLocation):Promise<void> {
    if (!this.active || this.location === location) {
      return;
    }

    const configurator = this.controller3D.engine.configurator;

    configurator.setVisible(this.currentGroupIDs, false);

    let visibilityGroupName:string;
    let camKey:CarModelCameraList;

    switch (location) {
      case XRayLocation.Car: {
        visibilityGroupName = "Xray Ground";
        camKey = CarModelCameraList.XRayCar;
        break;
      }
      case XRayLocation.Seat: {
        visibilityGroupName = "Xray Seat";
        camKey = CarModelCameraList.XRaySeat;
        break;
      }
      case XRayLocation.Trunk: {
        visibilityGroupName = "Xray Trunk";
        camKey = CarModelCameraList.XRayTrunk;
        break; 
      }
      case XRayLocation.Battery: {
        visibilityGroupName = "Xray Battery";
        camKey = CarModelCameraList.XRayBattery;
        break;
      }
      default: {
        throw `bad XRay location: ${XRayLocation[location]}`;
      }
    }

    const visibilityGroup = configurator.config.visibility_groups.find(g => g.name === visibilityGroupName);
    if (!visibilityGroup) {
      throw `Cannot find group: ${visibilityGroupName}`;
    }

    this.currentGroupIDs = visibilityGroup.grouping_ids;
    configurator.setVisible(this.currentGroupIDs, true);
    this.controller3D.setCamera(camKey);
    this._location = location;
  }

  public rotateRight():void {
    let index = LOCATIONS.indexOf(this.location);

    while (true) {
      const location = LOCATIONS[++index % LOCATIONS.length];
      if (this.isEnabledLocation(location)) {
        this.setLocation(location);
        break;
      }
    }
  }

  public rotateLeft():void {
    let index = LOCATIONS.indexOf(this.location);

    while (true) {
      index = (index + LOCATIONS.length - 1) % LOCATIONS.length;

      const location = LOCATIONS[index];
      if (this.isEnabledLocation(location)) {
        this.setLocation(location);
        break;
      }
    }
  }

  private changeEnvironment():void {
    const bgName = "BG X-Ray";
    const envName = "Light Studio";

    const configurator = this.controller3D.engine.configurator;

    // TODO: is there a better way that doesn't rely on naming convention?
    const bgSelectionGroup  = this.modelController.backgroundSelectionGroup;
    const envSelectionGroup = this.modelController.environmentGroup;

    const bgOption = bgSelectionGroup.selections.find(s => s.name === bgName);
    if (!bgOption) {
      throw `Cannot find background selection: ${bgName}`;
    }
    if (bgSelectionGroup.type !== "material") {
      throw `Background selection group must have type 'material'`;
    }
    configurator.setMaterialGroup(bgSelectionGroup.groupID, bgOption.id);

    const envOption = envSelectionGroup.selections.find(s => s.name === envName);
    if (!envOption) {
      throw `Cannot find environment selection: ${envName}`;
    }
    if (envSelectionGroup.type !== "state" && envSelectionGroup.type !== "environment") {
      throw `Environment selection group must have type 'state' or 'environment'`;
    }

    // NOTE: this will always pick the daylight/non-dimmed environment.
    // But is that the desired behaviour?
    this.controller3D.changeStateByName(envOption.id); // yes, this is correct.
  }
}